import { useMemo } from "react";

import { useBreakpoints } from "~/hooks/useBreakpoints";

import { SheetModal } from "../sheetModal/SheetModal";
import { DialogModal } from "../dialogModal/DialogModal";
import { Button } from "../button/Button";
import { Text } from "../text/Text";
import { ButtonStack } from "../button/ButtonStack";

import "./OnboardingModal.css";

export interface OnboardingModalProps {
  artwork?: string;
  isOpen?: boolean;
  title: string;
  description: string;
  disclaimer?: string;
  primaryActionLabel: string;
  secondaryActionLabel?: string;
  onRequestClose?: () => void;
  onUnmount?: () => void;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
}

export function OnboardingModal({
  artwork,
  isOpen,
  title,
  description,
  disclaimer,
  primaryActionLabel,
  secondaryActionLabel,
  onRequestClose,
  onUnmount,
  onPrimaryAction,
  onSecondaryAction,
}: OnboardingModalProps) {
  const breakpoints = useBreakpoints();

  const content = useMemo(() => {
    return (
      <div className="OnboardingModal">
        <div className="OnboardingModal__body">
          {artwork && (
            <img
              className={"OnboardingModal__artwork"}
              src={artwork}
              alt={title}
            />
          )}

          <Text size="3xl" weight="semibold" lineHeight="tight">
            {title}
          </Text>

          <Text color="gray">{description}</Text>

          {disclaimer && <Text weight="bold">{disclaimer}</Text>}
        </div>

        <ButtonStack>
          <Button appearance="primary" fill onClick={onPrimaryAction}>
            {primaryActionLabel}
          </Button>

          {secondaryActionLabel && (
            <Button
              appearance="secondary"
              fill
              onClick={onSecondaryAction ?? onRequestClose}
            >
              {secondaryActionLabel}
            </Button>
          )}
        </ButtonStack>
      </div>
    );
  }, [
    artwork,
    description,
    disclaimer,
    onPrimaryAction,
    onRequestClose,
    onSecondaryAction,
    primaryActionLabel,
    secondaryActionLabel,
    title,
  ]);

  return breakpoints.lg ? (
    <DialogModal
      isOpen={isOpen}
      size="sm"
      onRequestClose={onRequestClose}
      onUnmount={onUnmount}
    >
      <DialogModal.Body>{content}</DialogModal.Body>
    </DialogModal>
  ) : (
    <SheetModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      onUnmount={onUnmount}
    >
      <SheetModal.Body>{content}</SheetModal.Body>
    </SheetModal>
  );
}
